import { IconButton } from "@mui/material";

import { VisibilityToggle } from "@/shared/components/VisibilityToggle";
import {
  CloseIcon,
  KeyboardArrowDownIcon,
  KeyboardArrowRightIcon,
} from "@/shared/foundation/icons/mui";
import { useDebouncedCallback } from "@/shared/hooks";
import {
  type PlotSeries,
  actions,
  useVizDispatch,
} from "@/shared/state/visualization";
import { seriesConfig } from "@/shared/state/visualization/identifiers";

import { Expandable } from "../../../Expandable";

import styles from "./PlotSeriesHeader.module.css";

interface PlotSeriesHeaderProps {
  /**
   * The label the panel's legend shows for this series, already qualified
   * with the source file's path when several series plot the same signal.
   */
  displayLabel: string;
  /**
   * Id of the element holding this row's config fields. Rendered as
   * `aria-controls` only while expanded: a collapsed row unmounts that
   * element, so the id would otherwise reference nothing.
   */
  fieldsId: string;
  isExpanded: boolean;
  onToggle: (seriesId: string) => void;
  panelId: string;
  plotSeries: PlotSeries;
}

export function PlotSeriesHeader({
  displayLabel,
  fieldsId,
  isExpanded,
  onToggle,
  panelId,
  plotSeries,
}: PlotSeriesHeaderProps) {
  const vizStateDispatch = useVizDispatch();

  const changeSeriesColor = useDebouncedCallback(function setSeriesColor(
    lineColor: string,
  ) {
    vizStateDispatch(
      actions.setSeriesConfig(panelId, plotSeries.id, { lineColor }),
    );
  }, 15);

  const removeSeries = () => {
    vizStateDispatch(actions.removeSeriesFromPlotPanel(panelId, plotSeries.id));
  };

  const setSeriesVisibility = (visible: boolean) => {
    vizStateDispatch(
      actions.setSeriesVisibility(panelId, plotSeries.id, visible),
    );
  };

  return (
    <div className={styles.container}>
      <input
        className={styles.colorSwatch}
        type="color"
        value={seriesConfig(plotSeries)?.lineColor ?? "#000000"}
        name={plotSeries.id}
        aria-label={`Change line color for ${displayLabel}`}
        onChange={(event) => {
          changeSeriesColor(event.target.value);
        }}
      />
      <Expandable<string>
        data={plotSeries.id}
        StartIcon={isExpanded ? KeyboardArrowDownIcon : KeyboardArrowRightIcon}
        onClick={onToggle}
        className={styles.expandToggle}
        aria-expanded={isExpanded}
        aria-controls={isExpanded ? fieldsId : undefined}
      >
        <span className={styles.title}>{displayLabel}</span>
      </Expandable>
      <VisibilityToggle
        className={styles.iconButton}
        on={plotSeries.visible}
        onToggle={setSeriesVisibility}
      />
      <IconButton className={styles.iconButton} onClick={removeSeries}>
        <CloseIcon fontSize="inherit" />
      </IconButton>
    </div>
  );
}
